import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { SiCodeigniter } from "react-icons/si";
import { Modal } from "@components/shared";
import { useTranslation } from "@hooks/useTranslation";

import FilterButton from "./filters/filter-button";
import FilterOption from "./filters/filter-option";
import FilterContainer from "./filters/filters-container";
import SortBy from "./filters/sort-by-filter";

type Category = {
  id: number;
  name: string;
};

type ExperienceCategory = {
  id: number;
  value: string;
};
type ExperienceSortOption = {
  id: "price-asc" | "price-desc" | "popularity";
  value: string;
};

export type ExperiencesFilterOptions = {
  categories?: ExperienceCategory[];
  sortOption?: ExperienceSortOption;
};

type ExperiencesFilterProps = {
  filters: ExperiencesFilterOptions;
  count: number;
  categories: Category[];
  onFiltersChange: (filters: ExperiencesFilterOptions) => void;
  onApplyFilters: (filters: ExperiencesFilterOptions) => void;
  onClearFilters: () => void;
};

export default function ExperiencesFilters({
  filters,
  count,
  categories,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: ExperiencesFilterProps) {
  const { t } = useTranslation();

  const router = useRouter();
  const showFilters = router.query.show_filters === "true";
  const [experiencesFilters, setExperiencesFilters] = useState(filters);

  const sortOptions: ExperienceSortOption[] = [
    {
      id: "price-asc",
      value: t("spotprofile.experiences.filters.sort_by_options.price_asc"),
    },
    {
      id: "popularity",
      value: t("spotprofile.experiences.filters.sort_by_options.popularity"),
    },
    {
      id: "price-desc",
      value: t("spotprofile.experiences.filters.sort_by_options.price_desc"),
    },
  ];

  useEffect(() => {
    setExperiencesFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (showFilters) {
      onFiltersChange(experiencesFilters);
    }
  }, [experiencesFilters, onFiltersChange, showFilters]);

  useEffect(
    () => {
      const initialFilters: ExperiencesFilterOptions = {};

      if (router.query.categories) {
        const filtersCategories = router.query.categories
          .toString()
          .split(",")
          .filter((c) => categories.some(({ id }) => id === Number(c)))
          .map(
            (category) =>
              ({
                id: Number(category),
                value: categories.find(({ id }) => id === Number(category))
                  ?.name,
              } as ExperienceCategory)
          );

        if (filtersCategories.length > 0)
          initialFilters.categories = filtersCategories;
      }

      if (router.query.sortOption) {
        const sortOption = sortOptions.find(
          ({ id }) => id === router.query.sortOption?.toString()
        );

        if (sortOption) initialFilters.sortOption = sortOption;
      }

      if (Object.keys(initialFilters).length) {
        setExperiencesFilters(initialFilters);

        if (!showFilters) {
          onApplyFilters(initialFilters);
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query.categories, router.query.sortOption]
  );
  return (
    <Modal isOpen={showFilters}>
      <FilterContainer
        title={t("spotprofile.experiences.title")}
        count={count}
        onApply={() => onApplyFilters(experiencesFilters)}
        onClear={() => {
          setExperiencesFilters({});
          onClearFilters();
        }}
      >
        <Categories
          categories={categories}
          onSelect={(category) =>
            setExperiencesFilters(({ categories, ...rest }) => ({
              ...rest,
              categories:
                categories && categories.length > 0
                  ? categories.some(({ id }) => id === category.id)
                    ? categories.filter((c) => c.id !== category.id)
                    : [...categories, category]
                  : [category],
            }))
          }
          selectedCategories={experiencesFilters.categories}
        />
        <SortBy
          sortOptions={sortOptions}
          onSelect={(option) =>
            setExperiencesFilters((prev) => ({
              ...prev,
              sortOption:
                option.id === prev.sortOption?.id
                  ? undefined
                  : (option as ExperienceSortOption),
            }))
          }
          by={experiencesFilters.sortOption}
        />
      </FilterContainer>
    </Modal>
  );
}

const Categories = ({
  categories,
  selectedCategories,
  onSelect,
}: {
  categories: Category[];
  selectedCategories?: ExperienceCategory[];
  onSelect: (category: ExperienceCategory) => void;
}) => {
  const { t } = useTranslation();
  return (
    <FilterOption name={t("spotprofile.experiences.filters.categories")}>
      <div className="grid justify-between grid-cols-2 gap-2 mb-3">
        {categories.map((category) => {
          const isActive = !!selectedCategories?.some(
            ({ id }) => id === category.id
          );

          return (
            <FilterButton
              key={category.id}
              isActive={isActive}
              onClick={() =>
                onSelect({
                  id: category.id,
                  value: category.name,
                })
              }
              className="!px-0.5 !w-full flex items-center"
            >
              <p
                className={`w-full text-center flex items-center justify-center ${
                  isActive ? "text-primary-color" : ""
                }`}
              >
                {category.id === 0 && (
                  <span
                    className={`${
                      isActive
                        ? "text-primary-color border-primary-color"
                        : "text-white border-white"
                    } border border-primary-color rounded-full relative right-1.5 p-0.5`}
                  >
                    <SiCodeigniter />
                  </span>
                )}
                {category.name}
              </p>
            </FilterButton>
          );
        })}
      </div>
    </FilterOption>
  );
};

// const SortBy = ({
//   sortOptions,
//   by,
//   onSelect,
// }: {
//   sortOptions: ExperienceSortOption[];
//   by?: ExperienceSortOption;
//   onSelect: (option: ExperienceSortOption) => void;
// }) => {
//   const { t } = useTranslation();

//   return (
//     <FilterOption name={t("spotprofile.experiences.filters.sort_by")}>
//       <div className="grid items-center justify-between grid-cols-2 gap-2 mb-3">
//         {sortOptions.map((option) => {
//           const isActive = option.id === by?.id;
//           return (
//             <FilterButton
//               key={option.id}
//               isActive={isActive}
//               onClick={() => onSelect(option)}
//               className="!w-full"
//             >
//               <p
//                 className={`text-center w-full ${
//                   isActive ? "text-primary-color" : ""
//                 }`}
//               >
//                 {option.value}
//               </p>
//             </FilterButton>
//           );
//         })}
//       </div>
//     </FilterOption>
//   );
// };
